import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, BarChart3, RefreshCw, Calendar, Package, ArrowUp, ArrowDown, 
  X, Search, Download, Filter
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import * as goodsTransferApi from '@/services/goodsTransferApi';

const GoodsTransferReport = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState([]);
  
  const [searchQuery, setSearchQuery] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedOperation, setSelectedOperation] = useState('all');
  const [operations, setOperations] = useState([]);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const today = new Date();
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    setEndDate(today.toISOString().split('T')[0]);
    setStartDate(thirtyDaysAgo.toISOString().split('T')[0]);
  }, []);

  const fetchReport = useCallback(async () => {
    if (!startDate || !endDate) return;
    try {
      setLoading(true);
      const [reportData, opsData] = await Promise.all([
        goodsTransferApi.getGtEnhancedSummary({
          start_date: startDate,
          end_date: endDate,
          search: searchQuery || undefined,
          operation: selectedOperation === 'all' ? undefined : selectedOperation
        }),
        goodsTransferApi.getGtOperations()
      ]);
      setReport(reportData.report || []);
      setOperations(opsData.operations || []);
    } catch (error) {
      toast.error('Failed to fetch report');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, searchQuery, selectedOperation]);

  useEffect(() => {
    if (startDate && endDate) fetchReport();
  }, [fetchReport, startDate, endDate]);

  const handleClearFilters = () => {
    setSearchQuery('');
    setSelectedOperation('all');
    const today = new Date();
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    setEndDate(today.toISOString().split('T')[0]);
    setStartDate(thirtyDaysAgo.toISOString().split('T')[0]);
  };

  const handleExportCSV = async () => {
    try {
      setExporting(true);
      const data = await goodsTransferApi.getGtTransactions({
        start_date: startDate,
        end_date: endDate,
        search: searchQuery || undefined,
        operation: selectedOperation === 'all' ? undefined : selectedOperation
      });
      const transactions = data.transactions || [];
      if (transactions.length === 0) { toast.warning('No data to export'); return; }
      const headers = ['Date', 'USN Code', 'Design Name', 'Operation', 'Staff ID', 'Inward', 'Outward'];
      const csvRows = [headers.join(',')];
      transactions.forEach(txn => csvRows.push([txn.date, txn.usn_code, `"${txn.design_name}"`, txn.operation, txn.staff_id, txn.inward, txn.outward].join(',')));
      const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `goods_transfer_report_${startDate}_to_${endDate}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success(`Exported ${transactions.length} transactions`);
    } catch (error) {
      toast.error('Failed to export CSV');
    } finally {
      setExporting(false);
    }
  };

  const formatDate = (dateStr) => new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  const totalInward = report.reduce((sum, r) => sum + r.inward, 0);
  const totalOutward = report.reduce((sum, r) => sum + r.outward, 0);
  const latestClosing = report.length > 0 ? report[report.length - 1].closing : 0;

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/sections/goods-transfer')} className="text-[#F9FAFB] hover:bg-white/10"><ArrowLeft className="w-5 h-5" /></Button>
            <div><h1 className="text-xl font-bold font-display">Reports</h1><p className="text-sm text-[#E8DCC8]">Goods Transfer</p></div>
          </div>
          <Button variant="outline" size="sm" onClick={fetchReport} className="border-white/20 text-white hover:bg-white/10"><RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /></Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        <Card className="border-[#E5DFD0] mb-6">
          <CardHeader className="pb-4"><CardTitle className="flex items-center gap-2 text-lg"><Filter className="w-5 h-5" />Filters</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              <div className="lg:col-span-2">
                <Label className="text-sm font-medium mb-1.5 block">Search (USN/Design)</Label>
                <div className="relative"><Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" /><Input placeholder="Search..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10" /></div>
              </div>
              <div><Label className="text-sm font-medium mb-1.5 block">From Date</Label><Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} /></div>
              <div><Label className="text-sm font-medium mb-1.5 block">To Date</Label><Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} /></div>
              <div>
                <Label className="text-sm font-medium mb-1.5 block">Operation</Label>
                <Select value={selectedOperation} onValueChange={setSelectedOperation}><SelectTrigger><SelectValue placeholder="All" /></SelectTrigger><SelectContent><SelectItem value="all">All Operations</SelectItem>{operations.map(op => (<SelectItem key={op} value={op}>{op}</SelectItem>))}</SelectContent></Select>
              </div>
            </div>
            <div className="flex items-center justify-end mt-4 pt-4 border-t border-[#E5DFD0] gap-2">
              <Button variant="outline" size="sm" onClick={handleClearFilters}><X className="w-4 h-4 mr-1" />Clear</Button>
              <Button variant="outline" size="sm" onClick={fetchReport} className="bg-[#2D5F4F] text-white"><Search className="w-4 h-4 mr-1" />Apply</Button>
              <Button variant="outline" size="sm" onClick={handleExportCSV} disabled={exporting} className="border-green-600 text-green-600"><Download className="w-4 h-4 mr-1" />{exporting ? 'Exporting...' : 'Export CSV'}</Button>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card className="border-[#E5DFD0]"><CardContent className="py-4"><div className="flex items-center justify-between"><div><p className="text-xs text-[#5A5F6E]">Current Stock</p><p className="text-2xl font-bold text-[#1A1F2E]">{latestClosing}</p><p className="text-xs text-gray-500">USNs</p></div><Package className="w-8 h-8 text-blue-500" /></div></CardContent></Card>
          <Card className="border-[#E5DFD0]"><CardContent className="py-4"><div className="flex items-center justify-between"><div><p className="text-xs text-[#5A5F6E]">Total Inward</p><p className="text-2xl font-bold text-green-600">+{totalInward}</p></div><ArrowDown className="w-8 h-8 text-green-500" /></div></CardContent></Card>
          <Card className="border-[#E5DFD0]"><CardContent className="py-4"><div className="flex items-center justify-between"><div><p className="text-xs text-[#5A5F6E]">Total Outward</p><p className="text-2xl font-bold text-red-600">-{totalOutward}</p></div><ArrowUp className="w-8 h-8 text-red-500" /></div></CardContent></Card>
          <Card className="border-[#E5DFD0]"><CardContent className="py-4"><div className="flex items-center justify-between"><div><p className="text-xs text-[#5A5F6E]">Operations</p><p className="text-2xl font-bold text-[#1A1F2E]">{operations.length}</p></div><BarChart3 className="w-8 h-8 text-purple-500" /></div></CardContent></Card>
        </div>

        {loading ? (<div className="flex items-center justify-center py-12"><RefreshCw className="w-8 h-8 animate-spin text-[#2D5F4F]" /></div>
        ) : report.length === 0 ? (<Card className="border-[#E5DFD0]"><CardContent className="py-12 text-center"><Calendar className="w-12 h-12 mx-auto text-gray-400 mb-4" /><h3 className="text-lg font-semibold">No Data</h3></CardContent></Card>
        ) : (
          <Card className="border-[#E5DFD0]">
            <CardHeader><CardTitle className="flex items-center gap-2"><Calendar className="w-5 h-5" />Day Wise Transaction Report<Badge variant="outline" className="ml-2">USN Count</Badge></CardTitle></CardHeader>
            <CardContent>
              <Table>
                <TableHeader><TableRow className="bg-gray-50"><TableHead>Date</TableHead><TableHead className="text-center">Opening</TableHead><TableHead className="text-center">Inward (+)</TableHead><TableHead className="text-center">Outward (-)</TableHead><TableHead className="text-center">Closing</TableHead></TableRow></TableHeader>
                <TableBody>
                  {report.slice().reverse().map((row) => (
                    <TableRow key={row.date}><TableCell className="font-medium">{formatDate(row.date)}</TableCell><TableCell className="text-center text-blue-600">{row.opening}</TableCell><TableCell className="text-center text-green-600">{row.inward > 0 ? `+${row.inward}` : '0'}</TableCell><TableCell className="text-center text-red-600">{row.outward > 0 ? `-${row.outward}` : '0'}</TableCell><TableCell className="text-center font-semibold text-blue-600">{row.closing}</TableCell></TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default GoodsTransferReport;
